// src/components/KidsApplicationsList.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import styled from 'styled-components';

const KidsApplicationsList = () => {
  const [applications, setApplications] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchApplications = async () => {
    try {
      const response = await axios.get('https://elexdondigitalacademy.com/api/get_kids_applications.php');
      if (response.data.success) {
        setApplications(response.data.data);
      } else {
        console.error('Failed to fetch applications:', response.data.error);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleStatusChange = async (id, currentStatus) => {
    const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';

    const result = await Swal.fire({
      title: 'Update Payment Status?',
      text: `Change payment status to "${newStatus.toUpperCase()}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#dc2626',
      confirmButtonText: 'Yes, update it!'
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: 'Processing...',
        text: 'Updating status and notifying admin...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      try {
        const response = await axios.post('https://elexdondigitalacademy.com/api/update_kids_payment_status.php', {
          id: id,
          status: newStatus
        });

        if (response.data.success) {
          Swal.fire('Updated!', response.data.message, 'success');
          // Update state locally
          setApplications(prev =>
            prev.map(app => (app.id === id ? { ...app, payment_status: newStatus } : app))
          );
        } else {
          Swal.fire('Error!', response.data.error || 'Failed to update status.', 'error');
        }
      } catch (error) {
        Swal.fire('Error!', 'An error occurred while connecting to the server.', 'error');
      }
    }
  };

  const filteredApps = applications.filter(app =>
    app.child_name?.toLowerCase().includes(search.toLowerCase()) ||
    app.parent_name?.toLowerCase().includes(search.toLowerCase()) ||
    app.reference_code?.toLowerCase().includes(search.toLowerCase()) ||
    app.course?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <PageContainer>
      <HeaderSection>
        <div>
          <h2>Kids Bootcamp Applications</h2>
          <p>Review registered students, parent details, and toggle payment statuses.</p>
        </div>
        <SearchInput
          type="text"
          placeholder="Search by child, parent, ref..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </HeaderSection>

      {loading ? (
        <LoadingState>Loading applications...</LoadingState>
      ) : filteredApps.length > 0 ? (
        <Grid>
          {filteredApps.map((app) => (
            <Card key={app.id}>
              <CardHeader>
                <div>
                  <span className="ref-label">{app.reference_code}</span>
                  <h4>{app.child_name} <small>({app.child_age} yrs, {app.gender})</small></h4>
                </div>
                <StatusBadge status={app.payment_status || 'pending'}>
                  {app.payment_status || 'pending'}
                </StatusBadge>
              </CardHeader>

              <InfoGrid>
                <InfoItem>
                  <span className="label">Course</span>
                  <span className="value course-highlight">{app.course}</span>
                </InfoItem>
                <InfoItem>
                  <span className="label">Parent Name</span>
                  <span className="value">{app.parent_name}</span>
                </InfoItem>
                <InfoItem>
                  <span className="label">Email Address</span>
                  <span className="value">{app.email_address}</span>
                </InfoItem>
                <InfoItem>
                  <span className="label">Phone Number</span>
                  <span className="value">{app.phone_number}</span>
                </InfoItem>
                <InfoItem>
                  <span className="label">School</span>
                  <span className="value">{app.child_school}</span>
                </InfoItem>
                <InfoItem>
                  <span className="label">City / Address</span>
                  <span className="value">{app.city} ({app.parent_address})</span>
                </InfoItem>
              </InfoGrid>

              <CardFooter>
                <span className="date">Registered: {new Date(app.created_at).toLocaleDateString()}</span>
                <ActionButton
                  status={app.payment_status || 'pending'}
                  onClick={() => handleStatusChange(app.id, app.payment_status || 'pending')}
                >
                  Mark as {app.payment_status === 'paid' ? 'Pending' : 'Paid'}
                </ActionButton>
              </CardFooter>
            </Card>
          ))}
        </Grid>
      ) : (
        <EmptyState>No applications found.</EmptyState>
      )}
    </PageContainer>
  );
};

export default KidsApplicationsList;

// --- Styled Components ---
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  font-family: inherit;
  padding: 20px;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  h2 {
    color: #0f172a;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0 0 4px 0;
  }

  p {
    color: #64748b;
    font-size: 0.95rem;
    margin: 0;
  }
`;

const SearchInput = styled.input`
  padding: 10px 16px;
  width: 300px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.95rem;
  outline: none;
  background-color: #ffffff;
  transition: all 0.2s ease;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #64748b;
  font-size: 1rem;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #64748b;
  font-size: 1rem;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;

  .ref-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    color: #f59e0b;
    font-weight: 700;
  }

  h4 {
    margin: 2px 0 0 0;
    color: #0f172a;
    font-size: 1.1rem;
    
    small {
      font-size: 0.85rem;
      color: #64748b;
      font-weight: normal;
    }
  }
`;

const StatusBadge = styled.span`
  background: ${({ status }) => (status === 'paid' ? '#dcfce7' : '#fef3c7')};
  color: ${({ status }) => (status === 'paid' ? '#16a34a' : '#d97706')};
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .label {
    font-size: 0.75rem;
    color: #64748b;
    text-transform: uppercase;
    font-weight: 600;
  }

  .value {
    font-size: 0.9rem;
    color: #1e293b;
    font-weight: 500;
    word-break: break-word;

    &.course-highlight {
      color: #2563eb;
      font-weight: 700;
    }
  }
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;

  .date {
    font-size: 0.8rem;
    color: #94a3b8;
  }
`;

const ActionButton = styled.button`
  padding: 8px 14px;
  background-color: ${({ status }) => (status === 'paid' ? '#f1f5f9' : '#2563eb')};
  color: ${({ status }) => (status === 'paid' ? '#475569' : '#ffffff')};
  border: 1px solid ${({ status }) => (status === 'paid' ? '#cbd5e1' : 'transparent')};
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ status }) => (status === 'paid' ? '#e2e8f0' : '#1d4ed8')};
  }
`;